import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiAlertCircle, FiArrowUp, FiLoader, FiRefreshCw, FiSearch } from 'react-icons/fi';
import JobCard from '../components/JobCard';
import JobSearchBar from '../components/JobSearchBar';
import '../scss/JobList.scss';
import http, { getUserFacingError, UserFacingErrorKind } from '../services/http';
import { JobCitySource } from '../utils/jobCity';

interface City {
  ms_city_id: number;
  ms_city_name: string;
}

interface Level {
  id?: number;
  name?: string | null;
}

type OptionalLevel = Level | string | null;

interface Position {
  position_loker_id: number;
  position_loker_name: string;
}

interface Job extends JobCitySource {
  id: number;
  title: string;
  type: string;
  salary_min: number;
  salary_max: number;
  show_salary?: boolean;
  posted_date: string;
  experience?: OptionalLevel;
  experience_level?: OptionalLevel;
  education?: OptionalLevel;
  education_type?: OptionalLevel;
  education_level?: OptionalLevel;
  skills: Level[];
  positions?: Position[];
}

interface PaginationData {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
}

type JobListViewState = 'loading' | 'ready' | 'empty' | 'unauthorized' | 'not_found' | 'error';

const PAGE_LIMIT = 10;
const INITIAL_PAGINATION: PaginationData = { total: 0, perPage: PAGE_LIMIT, currentPage: 1, lastPage: 1 };

const SkeletonCard = () => (
  <div className="job-card job-card--skeleton" aria-hidden="true">
    <div className="skeleton skeleton--title" />
    <div className="skeleton skeleton--text" />
    <div className="skeleton skeleton--text skeleton--short" />
    <div className="skeleton-row">
      <div className="skeleton skeleton--badge" />
      <div className="skeleton skeleton--badge" />
    </div>
  </div>
);

const dedupeJobs = (items: Job[]) => {
  const map = new Map<number, Job>();
  items.forEach((item) => map.set(item.id, item));
  return Array.from(map.values());
};

const JobList: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [pagination, setPagination] = useState<PaginationData>(INITIAL_PAGINATION);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [observerReady, setObserverReady] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [appliedJobIds, setAppliedJobIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewState, setViewState] = useState<JobListViewState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [appendError, setAppendError] = useState<string | null>(null);
  const [filterOptionsNotice, setFilterOptionsNotice] = useState<string | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const pendingAppendPageRef = useRef<number | null>(null);
  const appendInFlightRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const isLoggedIn = Boolean(localStorage.getItem('authToken'));

  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const applyJobErrorState = useCallback((kind: UserFacingErrorKind, message: string) => {
    if (kind === 'auth') {
      setViewState('unauthorized');
      setError(message);
      return;
    }

    if (kind === 'not_found') {
      setViewState('not_found');
      setError(message);
      return;
    }

    setViewState('error');
    setError(message);
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setFilterOptionsNotice(null);
        const cityResponse = await http.get('public/city', {
          params: {
            order: [{ field: 'ms_city_name', sort: 'asc' }],
          },
        });
        const cityData = Array.isArray(cityResponse.data)
          ? cityResponse.data
          : cityResponse.data?.data;

        setCities(
          (Array.isArray(cityData) ? cityData : [])
            .map((city: any) => ({
              ms_city_id: Number(city?.ms_city_id),
              ms_city_name: String(city?.ms_city_name || '').trim(),
            }))
            .filter((city: City) => Number.isFinite(city.ms_city_id) && Boolean(city.ms_city_name))
        );
      } catch (fetchError) {
        console.error('Failed to load city options', fetchError);
        setFilterOptionsNotice(
          'Lokasi belum bisa dimuat sepenuhnya. Pencarian lowongan tetap bisa dipakai tanpa filter tambahan.'
        );
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setAppliedJobIds([]);
      return;
    }

    http
      .get<{ status: boolean; data: number[] }>('auth/applied-jobs')
      .then((response) => setAppliedJobIds(response.data?.data || []))
      .catch(() => setAppliedJobIds([]));
  }, [isLoggedIn]);

  const fetchJobs = useCallback(
    async (page = 1, mode: 'replace' | 'append' = 'replace') => {
      const isAppend = mode === 'append';
      const requestGeneration = isAppend
        ? requestGenerationRef.current
        : requestGenerationRef.current + 1;

      if (isAppend) {
        if (appendInFlightRef.current || pendingAppendPageRef.current === page) return;
        appendInFlightRef.current = true;
        pendingAppendPageRef.current = page;
        setLoadingMore(true);
        setAppendError(null);
      } else {
        requestGenerationRef.current = requestGeneration;
        appendInFlightRef.current = false;
        pendingAppendPageRef.current = null;
        setPage(1);
        setHasMore(true);
        setObserverReady(false);
        setLoading(true);
        setViewState('loading');
        setError(null);
        setAppendError(null);
        setJobs([]);
        setPagination(INITIAL_PAGINATION);
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }

      try {
        const params = new URLSearchParams();
        if (searchInput) params.append('search', searchInput);
        if (selectedLocationId !== null) params.append('city_id', String(selectedLocationId));
        params.append('page', String(page));
        params.append('limit', String(PAGE_LIMIT));

        const response = await http.get<{ data: Job[]; pagination: PaginationData }>(`jobs?${params.toString()}`);
        if (requestGeneration !== requestGenerationRef.current) return;

        const nextJobs = Array.isArray(response.data.data) ? response.data.data : [];
        const nextPagination = response.data.pagination || INITIAL_PAGINATION;
        const total = Number(nextPagination.total ?? 0);
        const perPage = Number(nextPagination.perPage ?? PAGE_LIMIT);
        const currentPage = Number(nextPagination.currentPage ?? page);
        const lastPage = Number(
          nextPagination.lastPage ?? Math.max(1, Math.ceil(total / Math.max(1, perPage)))
        );

        setPagination({
          total,
          perPage,
          currentPage,
          lastPage,
        });
        setPage(currentPage);
        setHasMore(nextJobs.length >= PAGE_LIMIT && currentPage < lastPage);

        if (isAppend) {
          setJobs((current) => dedupeJobs([...current, ...nextJobs]));
          if (nextJobs.length > 0) setViewState('ready');
        } else {
          setJobs(nextJobs);
          setViewState(nextJobs.length > 0 ? 'ready' : 'empty');
        }
      } catch (fetchError) {
        if (requestGeneration !== requestGenerationRef.current) return;

        const userFacingError = getUserFacingError(fetchError, 'Gagal memuat lowongan. Silakan coba lagi.');

        if (isAppend) {
          setAppendError(userFacingError.message);
        } else {
          applyJobErrorState(userFacingError.kind, userFacingError.message);
        }
      } finally {
        if (isAppend) {
          if (requestGeneration === requestGenerationRef.current) {
            appendInFlightRef.current = false;
            pendingAppendPageRef.current = null;
            setLoadingMore(false);
          }
        } else {
          if (requestGeneration === requestGenerationRef.current) {
            setLoading(false);
          }
        }
      }
    },
    [applyJobErrorState, searchInput, selectedLocationId]
  );

  useEffect(() => {
    fetchJobs(1, 'replace');
  }, [fetchJobs]);

  const handleSearch = (term: string, locationId: number | null) => {
    setSearchInput(term);
    setSelectedLocationId(locationId);
  };

  const loadNextPage = useCallback(() => {
    if (
      !hasMore ||
      loading ||
      loadingMore ||
      appendInFlightRef.current ||
      viewState !== 'ready' ||
      appendError
    ) {
      return;
    }

    fetchJobs(page + 1, 'append');
  }, [appendError, fetchJobs, hasMore, loading, loadingMore, page, viewState]);

  useEffect(() => {
    if (observerReady || loading || viewState !== 'ready' || !hasMore) return;

    const enableObserverAfterScroll = () => {
      if (window.scrollY > 24) {
        setObserverReady(true);
      }
    };

    window.addEventListener('scroll', enableObserverAfterScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', enableObserverAfterScroll);
    };
  }, [hasMore, loading, observerReady, viewState]);

  useEffect(() => {
    let frameId = 0;

    const updateBackToTopVisibility = () => {
      frameId = 0;
      setShowBackToTop(window.scrollY > 420);
    };

    const handleScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(updateBackToTopVisibility);
    };

    updateBackToTopVisibility();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!observerReady || !sentinel || !hasMore || loading || viewState !== 'ready' || appendError) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: '160px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [appendError, hasMore, loadNextPage, loading, observerReady, viewState]);

  const totalLoaded = jobs.length;
  const resultSummary = useMemo(() => {
    if (pagination.total === 0) return 'Belum ada lowongan yang ditampilkan';
    return `Menampilkan ${totalLoaded} dari ${pagination.total} lowongan`;
  }, [pagination.total, totalLoaded]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="jl-bg">
      <JobSearchBar onSearch={handleSearch} cities={cities} isLoading={loading && jobs.length === 0} />

      <div className="jl-container">
        {filterOptionsNotice && (
          <div className="jl-inline-notice">
            <FiAlertCircle />
            <p>{filterOptionsNotice}</p>
          </div>
        )}

        <div className="jl-page-shell">
          {!loading && (
            <div className="jl-page-head jl-page-head--compact">
              <p className="jl-result-count">{resultSummary}</p>
            </div>
          )}

          {loading && jobs.length === 0 && (
            <div className="jl-grid">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          )}

          {!loading && viewState === 'unauthorized' && (
            <div className="jl-state jl-state--error">
              <FiAlertCircle />
              <p>{error || 'Sesi Anda berakhir. Silakan login kembali.'}</p>
            </div>
          )}

          {!loading && viewState === 'not_found' && (
            <div className="jl-state jl-state--error">
              <FiSearch />
              <p>{error || 'Lowongan tidak ditemukan.'}</p>
            </div>
          )}

          {!loading && viewState === 'error' && error && (
            <div className="jl-state jl-state--error">
              <FiAlertCircle />
              <p>{error}</p>
              <button type="button" onClick={() => fetchJobs(1, 'replace')}>
                Coba lagi
              </button>
            </div>
          )}

          {!loading && viewState === 'empty' && (
            <div className="jl-state jl-state--empty">
              <FiSearch />
              <p>Tidak ada lowongan yang cocok dengan pencarianmu saat ini.</p>
              <p className="jl-state__sub">Coba kata kunci atau lokasi lain biar hasilnya lebih luas.</p>
            </div>
          )}

          {!loading && viewState === 'ready' && jobs.length > 0 && (
            <>
              <div className="jl-grid">
                {jobs.map((job) => (
                  <JobCard key={job.id} job={job} isApplied={appliedJobIds.includes(job.id)} />
                ))}
              </div>

              <div className="jl-list-footer">
                {appendError && (
                  <div className="jl-list-footer__error">
                    <FiAlertCircle />
                    <span>{appendError}</span>
                    <button type="button" onClick={() => fetchJobs(page + 1, 'append')}>
                      <FiRefreshCw />
                      <span>Coba lagi</span>
                    </button>
                  </div>
                )}

                {!appendError && loadingMore && (
                  <div className="jl-list-footer__loading">
                    <FiLoader className="spin" />
                    <span>Memuat lowongan berikutnya...</span>
                  </div>
                )}

                {!appendError && !loadingMore && !hasMore && (
                  <div className="jl-list-footer__complete">
                    <div className="jl-list-footer__end">Semua lowongan sudah ditampilkan.</div>
                  </div>
                )}

                {hasMore && <div ref={loadMoreSentinelRef} className="jl-load-sentinel" aria-hidden="true" />}
              </div>
            </>
          )}
        </div>
      </div>

      {!loading && viewState === 'ready' && jobs.length > 0 && !hasMore && showBackToTop && (
        <button
          type="button"
          className="jl-back-to-top"
          onClick={scrollToTop}
          aria-label="Kembali ke atas"
          title="Kembali ke atas"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default JobList;
